import { Mail, MessageCircle, Phone, RotateCw, Sparkles } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";

import { cn } from "@/lib/utils";

import type { NextActionType, Population, Temperature } from "../types";
import { acquisitionLabel } from "./acquisition";

// Sprint 4 T4.4.a — Badges Population / Température / Prochaine action,
// extraits depuis ContactListContent.tsx (Sprint 3 T3.2) pour être
// réutilisés par TodayCommercialView (T4.4.b) et autres consommateurs
// futurs. Couleurs alignées sur ContactHeader.tsx et TemperaturePopover.tsx.

const POPULATION_PILL: Record<Population, string> = {
  plateforme:
    "bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300",
  prospection:
    "bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300",
};
const POPULATION_LABEL: Record<Population, string> = {
  plateforme: "Plateforme",
  prospection: "Prospection",
};

const TEMPERATURE_PILL: Record<Temperature, string> = {
  chaud: "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300",
  a_chauffer:
    "bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300",
  froid: "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  perdu: "bg-muted text-muted-foreground",
};
const TEMPERATURE_LABEL: Record<Temperature, string> = {
  chaud: "Chaud",
  a_chauffer: "À chauffer",
  froid: "Froid",
  perdu: "Perdu",
};

const NEXT_ACTION_ICON: Record<
  Exclude<NextActionType, "aucune">,
  typeof Phone
> = {
  a_relancer: RotateCw,
  a_appeler: Phone,
  a_mailer: Mail,
  a_engager: MessageCircle,
};
const NEXT_ACTION_LABEL: Record<Exclude<NextActionType, "aucune">, string> = {
  a_relancer: "Relance",
  a_appeler: "Appel",
  a_mailer: "Démo",
  a_engager: "Engager",
};

export const PopulationBadge = ({
  value,
}: {
  value: Population | null | undefined;
}) => {
  if (!value) {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium",
        POPULATION_PILL[value],
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          value === "plateforme" ? "bg-green-500" : "bg-orange-500",
        )}
      />
      {POPULATION_LABEL[value]}
    </span>
  );
};

export const TemperatureBadge = ({
  value,
}: {
  value: Temperature | null | undefined;
}) => {
  if (!value) {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium",
        TEMPERATURE_PILL[value],
      )}
    >
      {TEMPERATURE_LABEL[value]}
    </span>
  );
};

export const NextActionBadge = ({
  type,
  date,
}: {
  type: NextActionType | null | undefined;
  date: string | null | undefined;
}) => {
  if (!type || type === "aucune") {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  const Icon = NEXT_ACTION_ICON[type] ?? Sparkles;
  const label = NEXT_ACTION_LABEL[type] ?? "Action";

  if (!date) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Icon className="size-3" />
        {label}
      </span>
    );
  }

  const target = new Date(date);
  const overdue = target.getTime() < Date.now();
  const rel = formatDistanceToNowStrict(target, {
    locale: fr,
    addSuffix: true,
  });
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        overdue ? "text-red-700 dark:text-red-300" : "text-foreground/80",
      )}
      title={`${label} · ${rel}`}
    >
      <Icon className="size-3 shrink-0" />
      <span className="truncate">
        {overdue ? "En retard" : `${label} ${rel}`}
      </span>
    </span>
  );
};

export const AcquisitionBadge = ({ value }: { value?: string | null }) => {
  if (value == null || value.trim() === "") return null;
  return (
    <span
      data-slot="acquisition-badge"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-fabrik-blue-soft text-fabrik-blue"
    >
      <span className="size-1.5 rounded-full bg-fabrik-blue" />
      {acquisitionLabel(value)}
    </span>
  );
};
